import React, { useState, useEffect } from 'react';
import { quizQuestions, calculatePreference } from '../quizData';
import logo from '../TBL Logo White.png';
import './UnderstandPreferences.css';

const UnderstandPreferences = ({ classVersion, onNext, onBack }) => {
  // Initialize with saved data or defaults
  const getInitialState = () => {
    const savedAnswers = sessionStorage.getItem('quizAnswers');
    const savedQuestionIndex = sessionStorage.getItem('quizQuestionIndex');
    const savedShowResults = sessionStorage.getItem('quizShowResults');
    
    console.log('UnderstandPreferences: Loading saved data:', {
      savedAnswers,
      savedQuestionIndex,
      savedShowResults
    });
    
    const state = {
      currentQuestionIndex: 0,
      answers: [],
      isLoading: false,
      showResults: false
    };
    
    if (savedAnswers) {
      try {
        const parsedAnswers = JSON.parse(savedAnswers);
        console.log('UnderstandPreferences: Parsed answers:', parsedAnswers);
        state.answers = parsedAnswers;
      } catch (error) {
        console.error('UnderstandPreferences: Error parsing saved answers:', error);
      }
    }
    if (savedQuestionIndex) {
      state.currentQuestionIndex = parseInt(savedQuestionIndex);
    }
    if (savedShowResults === 'true') {
      state.showResults = true;
    }
    
    return state;
  };

  const initialState = getInitialState();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(initialState.currentQuestionIndex);
  const [answers, setAnswers] = useState(initialState.answers);
  const [isLoading, setIsLoading] = useState(initialState.isLoading);
  const [showResults, setShowResults] = useState(initialState.showResults);

  // Save answers to session storage whenever they change
  useEffect(() => {
    console.log('UnderstandPreferences: Saving data:', {
      answers,
      currentQuestionIndex,
      showResults
    });
    sessionStorage.setItem('quizAnswers', JSON.stringify(answers));
    sessionStorage.setItem('quizQuestionIndex', currentQuestionIndex.toString());
    sessionStorage.setItem('quizShowResults', showResults.toString());
  }, [answers, currentQuestionIndex, showResults]);

  const handleAnswer = (answer) => {
    // Only add to answers if it's not a breaker screen (continue action)
    if (answer.value !== 'continue') {
      const newAnswers = [...answers, answer];
      setAnswers(newAnswers);
    }
    
    if (currentQuestionIndex < quizQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setIsLoading(true);
      // Simulate loading time
      setTimeout(() => {
        setIsLoading(false);
        setShowResults(true);
      }, 1000);
    }
    window.scrollTo(0, 0);
  };

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setIsLoading(false);
    setShowResults(false);
    // Clear session storage
    sessionStorage.removeItem('quizAnswers');
    sessionStorage.removeItem('quizQuestionIndex');
    window.scrollTo(0, 0);
    sessionStorage.removeItem('quizShowResults');
  };

  const handleFinish = () => {
    // Ensure preferences are saved
    const acidityPreference = calculatePreference(answers, 'acidity');
    const sweetnessPreference = calculatePreference(answers, 'sweetness');
    const bodyPreference = calculatePreference(answers, 'body');

    const preferences = {
      acidity: acidityPreference,
      sweetness: sweetnessPreference,
      body: bodyPreference
    };
    sessionStorage.setItem('winePreferences', JSON.stringify(preferences));
    
    // Go back to home
    onNext();
  };

  if (isLoading) {
    return (
      <div className={`understand-preferences ${classVersion}-theme`}>
        <div className="loading-screen">
          <img src={logo} className="loading-logo" alt="TBL Logo" />
          <h2>Calculating your preferences...</h2>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (showResults) {
    const acidityPreference = calculatePreference(answers, 'acidity');
    const sweetnessPreference = calculatePreference(answers, 'sweetness');
    const bodyPreference = calculatePreference(answers, 'body');

    // Save preferences to session storage
    const preferences = {
      acidity: acidityPreference,
      sweetness: sweetnessPreference,
      body: bodyPreference
    };
    sessionStorage.setItem('winePreferences', JSON.stringify(preferences));

    const getPreferenceLabel = (category, preference, score) => {
      const baseLabels = {
        acidity: { high: 'High Acidity', medium: 'Medium Acidity', low: 'Neutral Acidity' },
        sweetness: { high: 'High Sweetness', medium: 'Medium Sweetness', low: 'Low Sweetness' },
        body: { full: 'Full Body', medium: 'Medium Body', light: 'Light Body' }
      };
      
      let baseLabel = baseLabels[category]?.[preference] || preference;
      
      // Add modifiers based on score ranges within each preference level
      // Low: 0-30, Medium: 31-60, High: 61-100
      // Within each level: first 1/3 = -, middle 1/3 = base, last 1/3 = +
      
      if (category === 'body') {
        if (preference === 'light') {
          // Light: 0-30 range
          if (score <= 10) baseLabel = 'Light -';
          else if (score >= 21) baseLabel = 'Light +';
        } else if (preference === 'medium') {
          // Medium: 31-60 range
          if (score <= 40) baseLabel = 'Medium -';
          else if (score >= 51) baseLabel = 'Medium +';
        } else if (preference === 'full') {
          // Full: 61-100 range
          if (score <= 73) baseLabel = 'Full -';
          else if (score >= 87) baseLabel = 'Full +';
        }
      } else {
        // For acidity and sweetness
        if (preference === 'low') {
          // Low: 0-30 range
          if (score <= 10) baseLabel = 'Low -';
          else if (score >= 21) baseLabel = 'Low +';
        } else if (preference === 'medium') {
          // Medium: 31-60 range
          if (score <= 40) baseLabel = 'Medium -';
          else if (score >= 51) baseLabel = 'Medium +';
        } else if (preference === 'high') {
          // High: 61-100 range
          if (score <= 73) baseLabel = 'High -';
          else if (score >= 87) baseLabel = 'High +';
        }
      }
      
      return baseLabel;
    };

    // Calculate numerical scores (0-100) based on quiz answers
    const calculateNumericalScore = (answers, category) => {
      const categoryAnswers = answers.filter(answer => answer.category === category);
      const values = categoryAnswers.map(answer => answer.value);
      
      // Count occurrences
      const counts = values.reduce((acc, val) => {
        acc[val] = (acc[val] || 0) + 1;
        return acc;
      }, {});
      
      // Calculate weighted score based on answers
      let totalScore = 0;
      let totalWeight = 0;
      
      Object.entries(counts).forEach(([value, count]) => {
        let score;
        if (category === 'body') {
          // Body: light=25, medium=50, full=100
          score = value === 'light' ? 25 : value === 'medium' ? 50 : 100;
        } else {
          // Acidity/Sweetness: low=25, medium=50, high=100
          score = value === 'low' ? 25 : value === 'medium' ? 50 : 100;
        }
        totalScore += score * count;
        totalWeight += count;
      });
      
      // Return average score, rounded to nearest integer
      return Math.round(totalScore / totalWeight);
    };

    const acidityScore = calculateNumericalScore(answers, 'acidity');
    const sweetnessScore = calculateNumericalScore(answers, 'sweetness');
    const bodyScore = calculateNumericalScore(answers, 'body');

    return (
      <div className={`understand-preferences ${classVersion}-theme`}>
        <div className="back-button-container">
          <button className="back-button" onClick={() => {
            setShowResults(false);
            window.scrollTo(0, 0);
          }}>
            ← Back to Quiz
          </button>
        </div>
        <div className="preferences-header">
          <img src={logo} className="preferences-logo" alt="TBL Logo" />
          <h1>Your Wine Preferences</h1>
          <p className="preferences-description">
            Based on your answers, here's what we discovered about your wine taste:
          </p>
        </div>

        <div className="preferences-table">
          <div className="table-wrapper">
            <table>
            <thead>
              <tr>
                <th>Characteristic</th>
                <th>Your Preference</th>
                <th>Level</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Acidity</td>
                <td>{getPreferenceLabel('acidity', acidityPreference, acidityScore)}</td>
                <td>
                  <div className="preference-display">
                    <div className="progress-bar-container">
                      <div className="progress-bar-track">
                        <div 
                          className="progress-bar-fill" 
                          style={{ width: `${acidityScore}%` }}
                        ></div>
                      </div>
                      <span className="preference-value">{acidityScore}</span>
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <td>Sweetness</td>
                <td>{getPreferenceLabel('sweetness', sweetnessPreference, sweetnessScore)}</td>
                <td>
                  <div className="preference-display">
                    <div className="progress-bar-container">
                      <div className="progress-bar-track">
                        <div 
                          className="progress-bar-fill" 
                          style={{ width: `${sweetnessScore}%` }}
                        ></div>
                      </div>
                      <span className="preference-value">{sweetnessScore}</span>
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <td>Body</td>
                <td>{getPreferenceLabel('body', bodyPreference, bodyScore)}</td>
                <td>
                  <div className="preference-display">
                    <div className="progress-bar-container">
                      <div className="progress-bar-track">
                        <div 
                          className="progress-bar-fill" 
                          style={{ width: `${bodyScore}%` }}
                        ></div>
                      </div>
                      <span className="preference-value">{bodyScore}</span>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
            </table>
          </div>
        </div>

        <div className="action-buttons">
          <button className="retake-button" onClick={resetQuiz}>
            Retake Quiz
          </button>
          <button className="next-button" onClick={handleFinish}>
            Back to Home
          </button>
        </div>

        <div className="footer">
          <div className="footer-separator"></div>
          <img src={logo} className="footer-logo" alt="TBL Logo" />
        </div>
      </div>
    );
  }

  // Render question screen
  const question = quizQuestions[currentQuestionIndex];
  const isBreaker = question.type === 'breaker';

  return (
    <div className={`understand-preferences ${classVersion}-theme`}>
      <div className="back-button-container">
        <button className="back-button" onClick={onBack}>
          ← Back to Home
        </button>
      </div>
      <div className="question-header">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${((currentQuestionIndex + 1) / quizQuestions.length) * 100}%` }}
          ></div>
        </div>
        <div className="question-counter">
          {isBreaker ? `Section ${Math.ceil((currentQuestionIndex + 1) / 7)} of 3` : `Question ${currentQuestionIndex + 1} of ${quizQuestions.length}`}
        </div>
      </div>

      <div className="question-content">
        {isBreaker ? (
          // Breaker screen layout
          <div className="breaker-content">
            <img src={logo} className="question-logo" alt="TBL Logo" />
            <h1 className="breaker-title">{question.title}</h1>
            <div className="breaker-instructions">
              {question.instructions.split('\n').map((line, index) => (
                <p key={index} className="instruction-line">
                  {line}
                </p>
              ))}
            </div>
            <button className="continue-button" onClick={() => handleAnswer({ value: 'continue' })}>
              Continue
            </button>
          </div>
        ) : (
          // Regular question layout
          <>
            <img src={logo} className="question-logo" alt="TBL Logo" />
            <h2 className="question-text">{question.question}</h2>
            {question.subtitle && <h3 className="question-subtitle">{question.subtitle}</h3>}
            
            <div className="options-container">
              {question.options.map((option, index) => (
                <button
                  key={index}
                  className="option-button"
                  onClick={() => handleAnswer({
                    questionId: question.id,
                    category: question.category,
                    value: option.value,
                    text: option.text
                  })}
                >
                  {option.text}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="footer">
        <div className="footer-separator"></div>
        <img src={logo} className="footer-logo" alt="TBL Logo" />
      </div>
    </div>
  );
};

export default UnderstandPreferences;
